import { Component, OnInit, Inject, signal, computed, DestroyRef, effect } from '@angular/core';
import {
  MatDialog,
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Ride, DaypartException, UserInRide, AutomaticallyPlannedRide } from 'src/app/interfaces';
import {
  doc,
  collection,
  getDocs,
  query,
  where,
  or,
  and,
  updateDoc,
  addDoc,
  setDoc,
} from 'firebase/firestore';
import moment from 'moment';
import { CopyDateSequenceComponent } from '../copy-date-sequence/copy-date-sequence.component';
import { db, dataStore } from 'src/app/app.component';
import {
  getCopiedLocationsOrder,
  getExceptionsForDay,
  getRideCopyVehicleError,
  getWeekString,
} from '../../helpers/ride-helper';
import { valueToDate } from 'src/app/globals';
import { addToReminderNotificationQueue } from '../../helpers/notification-helper';
import { PlanType } from 'src/app/enums';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatError, MatSuffix, MatHint } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatChipListbox, MatChip } from '@angular/material/chips';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';

export interface DialogData {
  ride: Ride | AutomaticallyPlannedRide;
  planType?: PlanType;
}

@Component({
  selector: 'app-copy-ride',
  templateUrl: './copy-ride.component.html',
  styleUrls: ['./copy-ride.component.scss'],
  imports: [
    MatDialogTitle,
    CdkDrag,
    CdkDragHandle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatError,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatHint,
    MatChipListbox,
    MatChip,
    MatTooltip,
    MatButton,
    MatProgressSpinner,
    MatDialogActions,
    MatDialogClose,
  ],
})
export class CopyRideComponent implements OnInit {
  copyForm: UntypedFormGroup = this.fb.group({
    type: ['weekly', Validators.required],
    times: [1, Validators.required],
    endDate: [
      moment(valueToDate(this.data.ride.start)).add(1, 'week').toDate(),
      this.maxDateValidator(),
    ],
  });
  copyInProgress = signal(false);
  copyBlocked = signal(false);
  organisationId = localStorage.getItem('orgId') as string;
  parentRideId: string;
  rebuildingSequence = signal(false);
  dateSequence = signal<string[]>([]);
  warnings = signal<Record<string, any>>({});
  exceptions = signal<DaypartException[]>([]);

  numBlockedDates = computed(() => {
    return this.dateSequence().filter((date) => !!this.warnings()[date]).length;
  });

  isAllBlocked = computed(() => {
    const total = this.dateSequence().length;
    return total > 0 && total === this.numBlockedDates();
  });

  copyDisabled = computed(() => {
    if (this.rebuildingSequence() || this.copyInProgress() || this.copyBlocked()) return true;
    if (!this.copyForm.valid || this.dateSequence().length === 0) return true;
    return this.isAllBlocked();
  });

  chipOverflow: boolean = false;
  moment = moment;
  Object = Object;
  dataStore = dataStore;
  collectionName: string = this.data?.planType === PlanType.Automatic ? 'automaticRides' : 'rides';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<CopyRideComponent>,
    private fb: UntypedFormBuilder,
    private snackbar: MatSnackBar,
    private destroyRef: DestroyRef,
  ) {
    effect(() => {
      if (this.rebuildingSequence() || this.copyInProgress()) {
        this.copyForm.disable({ emitEvent: false });
      } else {
        this.copyForm.enable({ emitEvent: false });
      }
    });
  }

  async ngOnInit() {
    this.parentRideId = this.data.ride.parentRideId ?? this.data.ride.id;
    const exceptionsSnapshot = await getDocs(
      collection(db, `organisations/${this.organisationId}/dayPartExceptions`),
    );
    const exceptions = [];
    exceptionsSnapshot.forEach((exceptionDoc) => {
      const data = exceptionDoc.data() as DaypartException;
      data.id = exceptionDoc.id;
      exceptions.push(data);
    });
    this.exceptions.set(exceptions);
    this.copyForm.controls.type.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.rebuildingSequence.set(true);
        this.calculateEndDateValue(this.copyForm.get('times')?.value);
      });
    this.copyForm.controls.times.valueChanges
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((times) => {
        if (!this.rebuildingSequence()) {
          this.rebuildingSequence.set(true);
          this.calculateEndDateValue(times);
        }
      });
    this.copyForm.controls.endDate.valueChanges
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((endDate) => {
        if (!this.rebuildingSequence()) {
          this.rebuildingSequence.set(true);
          this.calculateTimesValue(endDate);
        }
      });
    this.calculateEndDateValue(this.copyForm.get('times')?.value);
  }

  async copy() {
    if (this.copyInProgress()) return;
    this.copyInProgress.set(true);
    if (this.data.planType !== PlanType.Automatic) {
      try {
        const vehicleError = await getRideCopyVehicleError(this.organisationId, this.data.ride);
        if (vehicleError) {
          this.copyBlocked.set(true);
          this.copyInProgress.set(false);
          this.dialog.open(WarningDialog, {
            data: { dialogType: 'rideCopyBlocked', optionalData: { exceptionName: vehicleError } },
          });
          return;
        }
      } catch (error) {
        this.copyInProgress.set(false);
        throw error;
      }
    }
    this.dialogRef.disableClose = true;

    let promises = [];
    let copiesMade = 0;
    const productsSnapshot = await getDocs(
      collection(db, `organisations/${this.organisationId}/products`),
    );
    const products = productsSnapshot.docs;

    const maxEndDateValue = this.copyForm.get('endDate')?.value;
    const maxEndDate = maxEndDateValue
      ? moment(maxEndDateValue).set({ hour: 23, minutes: 59 })
      : moment().set({ hour: 23, minutes: 59 }).add(2, 'year');
    maxEndDate.weekday(7);

    for (let i = 1; i < (this.copyForm.get('times')?.value || 0) + 1; i++) {
      const dates = this.calculateRideDates(this.data.ride, i);
      const dateString = moment(dates.start).format('DD-MM-YYYY');

      if (this.hasWarning(dateString) || maxEndDate.isBefore(moment(dates.start))) continue;

      const ride = structuredClone(this.data.ride) as Ride;
      ride.start = dates.start;
      ride.end = dates.end;
      ride.hasCopy = true;
      delete ride.remarkFromDriver;

      if (getExceptionsForDay(ride.start, ride.end, this.exceptions())) continue;

      const newRideId = (
        await addDoc(
          collection(db, `organisations/${this.organisationId}/${this.collectionName}`),
          {},
        )
      ).id;

      this.updateRideUsersDates(ride);
      this.refreshRideProducts(ride, products);

      if (this.data.planType !== PlanType.Automatic) {
        this.applyDateMetaData(ride);
        promises.push(this.createEditHistoryRecord(newRideId));
      }

      ride.parentRideId = this.parentRideId;
      promises.push(
        setDoc(
          doc(db, `organisations/${this.organisationId}/${this.collectionName}/${newRideId}`),
          { ...ride, id: newRideId },
          { merge: true },
        ),
      );

      if (ride.users) {
        for (const user of Object.values(ride.users) as UserInRide[]) {
          promises.push(addToReminderNotificationQueue(newRideId, user, this.organisationId));
        }
      }

      if (promises.length > 100) {
        await Promise.allSettled(promises);
        promises = [];
      }
      copiesMade++;
    }

    if (promises.length > 0) await Promise.allSettled(promises);

    if (copiesMade > 0) {
      await updateDoc(
        doc(db, `organisations/${this.organisationId}/${this.collectionName}/${this.data.ride.id}`),
        { parentRideId: this.parentRideId, hasCopy: true },
      );
    }

    this.copyInProgress.set(false);
    this.dialogRef.disableClose = false;
    this.dialogRef.close();
    this.snackbar.open(`De rit is ${copiesMade} keer gekopieerd.`, 'X', { duration: 5000 });
  }

  private updateRideUsersDates(ride: Ride) {
    if (!ride.users || Object.keys(ride.users).length === 0) return;

    const updatedUsers = {};
    for (const user of Object.values(ride.users) as UserInRide[]) {
      const pickup = moment(valueToDate(user.userPickupTime));
      const dropoff = moment(valueToDate(user.userDropoffTime));

      user.userPickupTime = moment(ride.start)
        .set({ hours: pickup.hours(), minutes: pickup.minutes() })
        .toDate();
      user.userDropoffTime = moment(ride.start)
        .set({ hours: dropoff.hours(), minutes: dropoff.minutes() })
        .toDate();

      delete user.remarkFromDriver;
      delete user.paid;
      delete user.charge;

      updatedUsers[user.id] = user;
    }
    ride.users = updatedUsers as any;
    ride.locationsOrder = getCopiedLocationsOrder(this.data.ride as Ride, ride);
  }

  private refreshRideProducts(ride: Ride, products: any[]) {
    const refresh = (item: any) => {
      if (!item?.rideProduct?.id) return;
      const match = products.find((p) => p.id === item.rideProduct.id);
      if (match) item.rideProduct = { ...match.data(), id: match.id };
    };

    refresh(ride);
    if (ride.users) {
      Object.values(ride.users).forEach((u) => refresh(u));
    }
  }

  private applyDateMetaData(ride: Ride) {
    ride.year = ride.start.getFullYear();
    ride.month = ride.start.getMonth();
    ride.week = getWeekString(ride.start);
    ride.day = ride.start.getDate();
  }

  private createEditHistoryRecord(newRideId: string) {
    return addDoc(
      collection(
        db,
        `organisations/${this.organisationId}/${this.collectionName}/${newRideId}/edits`,
      ),
      {
        changes: [{ field: 'copiedRide', text: 'Rit aangemaakt door middel van kopiëren' }],
        date: new Date(),
        editedByEmail: dataStore.rootUser.email,
        editedByUid: dataStore.rootUser.id,
      },
    );
  }

  async buildDateSequence() {
    this.rebuildingSequence.set(true);
    this.warnings.set({});
    const dateSequence: string[] = [];
    this.dateSequence.set([]);
    const times = this.copyForm.get('times')?.value || 0;
    for (let i = 1; i < times + 1; i++) {
      const dateObj = this.calculateRideDates(this.data.ride, i);
      const dateString = moment(dateObj.start).format('DD-MM-YYYY');
      dateSequence.push(dateString);
      await this.getWarning(dateObj);
    }
    this.dateSequence.set(dateSequence);

    if (this.dateSequence().length > 0 && this.copyForm.get('type')?.value === 'monthly') {
      const lastDate = moment(dateSequence.slice(-1)[0], 'DD-MM-YYYY');
      this.copyForm.controls.endDate.setValue(lastDate.toDate(), { emitEvent: false });
      this.copyForm.markAllAsTouched();
    }
    this.rebuildingSequence.set(false);
  }

  viewDateSequence() {
    this.dialog.open(CopyDateSequenceComponent, {
      width: '475px',
      autoFocus: false,
      data: {
        type: 'ride',
        dateSequence: this.dateSequence(),
        warnings: this.warnings(),
      },
    });
  }

  calculateRideDates(ride: Ride | AutomaticallyPlannedRide, i = 1) {
    const type = this.copyForm.get('type')?.value;
    const originalStart = moment(valueToDate(ride.start));
    const originalEnd = moment(valueToDate(ride.end));

    const rideStartDate = originalStart.clone();
    const rideEndDate = originalEnd.clone();
    if (type === 'monthly') {
      rideStartDate.add(i, 'month');
      rideEndDate.add(i, 'month');
      this.balanceMonthDate(rideStartDate, originalStart);
      this.balanceMonthDate(rideEndDate, originalEnd);
    } else {
      const steps = type === 'everyotherweek' ? i * 2 : i;
      const unit = type === 'daily' ? 'day' : 'week';
      rideStartDate.add(steps, unit);
      rideEndDate.add(steps, unit);
    }

    return { start: rideStartDate.toDate(), end: rideEndDate.toDate() };
  }

  private balanceMonthDate(date: moment.Moment, originalDate: moment.Moment) {
    const targetMonth = date.month();
    date.date(originalDate.date()).day(originalDate.day());

    // check if month has changed because of the day enforcement. If yes, modify date so it falls within the correct month.
    const isAhead = date.month() > targetMonth || (targetMonth === 11 && date.month() === 0);
    const isBehind = date.month() < targetMonth || (targetMonth === 0 && date.month() === 11);

    if (isAhead) {
      date.subtract(1, 'week');
    } else if (isBehind) {
      date.add(1, 'week');
    }
  }

  calculateTimesValue(endDate) {
    if (
      this.copyForm.controls.endDate.valid &&
      endDate !== null &&
      moment(endDate).isSameOrAfter(moment(valueToDate(this.data.ride.start)))
    ) {
      const rideStart = moment(valueToDate(this.data.ride.start)).set({ hour: 0, minutes: 0 });
      const type = this.copyForm.get('type')?.value;
      let times = 0;

      if (type === 'monthly') {
        for (let i = 1; ; i++) {
          const next = rideStart.clone().add(i, 'month');
          this.balanceMonthDate(next, rideStart);
          if (next.isAfter(moment(endDate))) break;
          times = i;
        }
      } else {
        const diffUnit = type === 'daily' ? 'day' : 'week';
        const diff = moment(endDate).diff(rideStart, diffUnit);
        times = type === 'everyotherweek' ? Math.floor(diff / 2) : diff;
      }

      this.copyForm.controls.times.setValue(Math.max(0, times || 0), { emitEvent: false });
      this.buildDateSequence();
    } else {
      this.dateSequence.set([]);
      this.copyForm.markAllAsTouched();
      this.rebuildingSequence.set(false);
    }
  }

  calculateEndDateValue(times) {
    if (times > 0) {
      const rideDateObj = this.calculateRideDates(this.data.ride, times);
      const endDate = moment(rideDateObj.start).set({ hour: 23, minutes: 59 });

      this.copyForm.controls.endDate.setValue(endDate.toDate(), { emitEvent: false });
      if (this.copyForm.controls.endDate.valid) {
        this.buildDateSequence();
      } else {
        this.dateSequence.set([]);
        this.copyForm.markAllAsTouched();
        this.rebuildingSequence.set(false);
      }
    } else {
      this.dateSequence.set([]);
      this.rebuildingSequence.set(false);
    }
  }

  hasWarning(dateString: string) {
    const warning = this.warnings()[dateString];
    if (!warning) return false;
    return warning.doubleBooking === true || warning.exception === true;
  }

  async getWarning(dateObj) {
    // ! When the double booking warning is deactivated, it's possible to do double bookings with copyride
    let preparedRideDocs = [];
    const dateString = moment(dateObj.start).format('DD-MM-YYYY');

    // Explicitly check for exceptions first
    const exception = getExceptionsForDay(dateObj.start, dateObj.end, this.exceptions());

    let constraint = or(
      // get rides where start of the new ride is found inside timeslot of an existing ride
      and(
        where('start', '<=', moment(dateObj.start).toDate()),
        where('end', '>', moment(dateObj.start).toDate()),
      ),
      // get rides where end of the new ride is found inside timeslot of an existing ride
      and(
        where('start', '<', moment(dateObj.end).toDate()),
        where('end', '>=', moment(dateObj.end).toDate()),
      ),
      // get rides where start of an existing ride is found inside timeslot of the new ride
      and(
        where('start', '>=', moment(dateObj.start).toDate()),
        where('start', '<', moment(dateObj.end).toDate()),
      ),
      // get rides where end of an existing ride is found inside timeslot of the new ride
      and(
        where('end', '>', moment(dateObj.start).toDate()),
        where('end', '<=', moment(dateObj.end).toDate()),
      ),
    );
    // ! When the double booking warning is deactivated, it's possible to do double bookings with copyride
    if (dataStore.generalSettings.doubleBookingActivated) {
      console.log('parrentrideID', this.data.ride.parentRideId);
      constraint = or(
        // get rides where start of the new ride is found inside timeslot of an existing ride
        and(
          where('start', '<=', moment(dateObj.start).toDate()),
          where('end', '>', moment(dateObj.start).toDate()),
          where('parentRideId', '==', this.data.ride.parentRideId ?? null),
        ),
        // get rides where end of the new ride is found inside timeslot of an existing ride
        and(
          where('start', '<', moment(dateObj.end).toDate()),
          where('end', '>=', moment(dateObj.end).toDate()),
          where('parentRideId', '==', this.data.ride.parentRideId ?? null),
        ),
        // get rides where start of an existing ride is found inside timeslot of the new ride
        and(
          where('start', '>=', moment(dateObj.start).toDate()),
          where('start', '<', moment(dateObj.end).toDate()),
          where('parentRideId', '==', this.data.ride.parentRideId ?? null),
        ),
        // get rides where end of an existing ride is found inside timeslot of the new ride
        and(
          where('end', '>', moment(dateObj.start).toDate()),
          where('end', '<=', moment(dateObj.end).toDate()),
          where('parentRideId', '==', this.data.ride.parentRideId ?? null),
        ),
      );
    }
    const rideDocs = (
      await getDocs(query(collection(db, `organisations/${this.organisationId}/rides`), constraint))
    ).docs;
    if (this.data?.planType === PlanType.Automatic) {
      preparedRideDocs = (
        await getDocs(
          query(collection(db, `organisations/${this.organisationId}/automaticRides`), constraint),
        )
      ).docs;
    }

    let vehicleBooked = false;
    let userBooked = false;
    let driverBooked = false;

    if (rideDocs.length > 0 || preparedRideDocs.length > 0) {
      const targetUserIds: string[] =
        this.data?.planType === PlanType.Automatic
          ? [(this.data.ride as AutomaticallyPlannedRide).passenger?.id].filter((id) => !!id)
          : (this.data.ride as Ride).userIds || [];

      for (const rideDoc of rideDocs) {
        const rideData = rideDoc.data() as Ride;

        if (rideData.vehicleId && rideData.vehicleId === this.data.ride.vehicleId) {
          vehicleBooked = true;
        }
        if (rideData.driverId && rideData.driverId === this.data.ride.driverId) {
          driverBooked = true;
        }
        if (rideData.userIds?.some((uid) => targetUserIds.includes(uid))) {
          userBooked = true;
        }

        if (vehicleBooked || driverBooked || userBooked) break;
      }

      if (!userBooked && this.data?.planType === PlanType.Automatic) {
        const passengerId = (this.data.ride as AutomaticallyPlannedRide).passenger?.id;
        userBooked = preparedRideDocs.some((doc) => {
          const d = doc.data();
          return !d.rejected && !d.planningCompleted && d.passenger?.id === passengerId;
        });
      }
    }

    const messages = [];
    if (vehicleBooked)
      messages.push('Het voertuig is op dit tijdstip al ingepland voor een andere rit.');
    if (userBooked)
      messages.push(
        'Een passagier in deze rit is op dit tijdstip al ingepland voor een andere rit.',
      );
    if (driverBooked)
      messages.push('Een chauffeur is op dit tijdstip al ingepland voor een andere rit.');

    if (exception) {
      messages.push(`Er is een uitzondering ingesteld voor ${exception.title} op deze datum.`);
    }

    if (messages.length > 0) {
      this.warnings.update((warnings) => ({
        ...warnings,
        [dateString]: {
          doubleBooking: vehicleBooked || userBooked || driverBooked,
          exception: !!exception,
          message: messages.join('\n'),
        },
      }));
    }
  }

  maxDateValidator(): ValidatorFn {
    return (c: AbstractControl): ValidationErrors | null => {
      if (!c.value) return null;
      const value = moment(c.value).set({ hour: 23, minutes: 59 });
      const max = moment().set({ hour: 23, minutes: 59 }).add(2, 'year');

      return value.isAfter(max) ? { maxDateExceeded: true } : null;
    };
  }
}
